import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { ArrowLeft, CheckCircle2, CircleSlash, FlaskConical, GitBranch, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Empty, LifecycleChip, Loading, Panel, SeverityChip, Table, Td, Th } from "@/components/common/Chrome";
import { VersionDiff } from "@/components/rules/VersionDiff";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LIFECYCLE_ORDER, dateTime, hashShort, shortDate } from "@/lib/format";

const NEXT_LABEL = {
  SOURCE_VERIFIED: "Mark source verified",
  ANALYST_REVIEW: "Send to analyst review",
  LEGAL_REVIEW: "Send to legal review",
  APPROVED: "Approve (sign)",
  PUBLISHED: "Publish",
  SUSPENDED: "Suspend",
  RETIRED: "Retire",
  DRAFT: "Return to draft",
};

export default function RuleDetail() {
  const { ruleId } = useParams();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    const [r, m] = await Promise.all([api.get(`/regulatory/rules/${ruleId}`), api.get("/regulatory/meta")]);
    setData(r.data);
    setMeta(m.data);
  }, [ruleId]);

  useEffect(() => {
    load().catch(() => setData(false));
  }, [load]);

  const transition = async (to) => {
    setBusy(to);
    try {
      await api.post(`/regulatory/rules/${ruleId}/transition`, { to_state: to, note: "" });
      toast.success(`Moved to ${to.replace(/_/g, " ")}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const runTests = async () => {
    setBusy("tests");
    try {
      const { data: run } = await api.post(`/regulatory/rules/${ruleId}/tests`);
      toast[run.passed ? "success" : "warning"](`${run.passed_count}/${run.total} automated tests passed`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const newVersion = async () => {
    setBusy("version");
    try {
      const { data: v } = await api.post(`/regulatory/rules/${ruleId}/new-version`);
      toast.success(`Created ${v.rule_key} v${v.version}`);
      window.location.href = `/regulatory/rules/${v.id}`;
    } catch (e) {
      toast.error(apiError(e));
      setBusy("");
    }
  };

  if (!data) return <Loading label="Loading rule version" />;
  const rule = data.rule;
  const nextStates = meta?.lifecycle_transitions?.[rule.lifecycle_state] || [];
  const lastRun = data.test_runs?.[0];

  return (
    <div className="space-y-6">
      <Link to="/regulatory/rules" data-testid="back-to-workbench"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3 w-3" /> Rule Workbench
      </Link>

      <div className="flex flex-col gap-4 border-b border-border pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-primary" data-testid="rule-detail-key">{rule.rule_key}</span>
            <span className="font-mono text-[11px] text-muted-foreground/70">v{rule.version}</span>
            <LifecycleChip state={rule.lifecycle_state} testId="rule-lifecycle-status-badge" />
            <SeverityChip severity={rule.severity} />
            {rule.immutable && <Chip className="border-border bg-muted text-muted-foreground">Immutable</Chip>}
            {rule.is_global_control && <Chip className="border-primary/40 bg-primary/10 text-primary">Global control</Chip>}
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{rule.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{rule.summary}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <VersionDiff versions={data.versions || []} currentId={rule.id} />
          <Link to="/regulatory/exceptions" data-testid="rule-exceptions-link">
            <Button variant="outline" size="sm" className="rounded-full border-border text-xs">
              <ShieldAlert className="mr-1.5 h-3 w-3" /> Exceptions
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={runTests} disabled={busy === "tests" || !can("rule", "write")}
                  data-testid="run-tests-button" className="rounded-full border-border text-xs">
            {busy === "tests" ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <FlaskConical className="mr-1.5 h-3 w-3" />}
            Run 6 tests
          </Button>
          {nextStates.map((s) => (
            <Button key={s} size="sm" onClick={() => transition(s)} disabled={Boolean(busy)}
                    data-testid={`transition-${s}`}
                    className={`rounded-full text-xs ${s === "PUBLISHED" ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : s === "SUSPENDED" || s === "RETIRED" ? "bg-red-600/90 hover:bg-red-500"
                      : "bg-muted hover:bg-muted"}`}>
              {busy === s ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : null}
              {NEXT_LABEL[s] || s}
            </Button>
          ))}
          {can("rule", "write") && (
            <Button variant="outline" size="sm" onClick={newVersion} disabled={Boolean(busy)}
                    data-testid="new-version-button" className="rounded-full border-border text-xs">
              <GitBranch className="mr-1.5 h-3 w-3" /> New version
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel testId="panel-lifecycle" className="xl:col-span-2" kicker="FR-REG-006" title="Lifecycle pipeline">
          <div className="flex flex-wrap items-center gap-1.5 p-4">
            {LIFECYCLE_ORDER.map((s, i) => {
              const done = LIFECYCLE_ORDER.indexOf(rule.lifecycle_state) >= i;
              const current = rule.lifecycle_state === s;
              return (
                <div key={s} className="flex items-center gap-1.5">
                  <div data-testid={`pipeline-${s}`}
                       className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors duration-200 ${
                         current ? "border-primary/40 bg-primary/15 text-primary"
                           : done ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                           : "border-border bg-muted/40 text-muted-foreground/70"}`}>
                    {s.replace(/_/g, " ")}
                  </div>
                  {i < LIFECYCLE_ORDER.length - 1 && <span className="text-muted-foreground/70">›</span>}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-px border-t border-border bg-muted sm:grid-cols-2">
            <Meta label="Regulator" value={rule.regulator || "—"} />
            <Meta label="Citation" value={rule.citation || "—"} mono />
            <Meta label="Effective from" value={shortDate(rule.effective_from)} mono />
            <Meta label="Effective to" value={rule.effective_to ? shortDate(rule.effective_to) : "Open-ended"} mono />
            <Meta label="Analyst review" value={rule.analyst_reviewed_by || "pending"} />
            <Meta label="Legal review" value={rule.legal_reviewed_by || "pending"} />
            <Meta label="Approved by" value={rule.approved_by || "pending"} />
            <Meta label="Approval signature" value={hashShort(rule.approval_signature)} mono />
          </div>
          {(rule.obligation || rule.remediation) && (
            <div className="space-y-2 border-t border-border p-4">
              {rule.obligation && (
                <p className="text-xs text-foreground/80"><span className="kicker mr-2">Obligation</span>{rule.obligation}</p>
              )}
              {rule.remediation && (
                <p className="text-xs text-muted-foreground"><span className="kicker mr-2">Remediation</span>{rule.remediation}</p>
              )}
            </div>
          )}
        </Panel>

        <Panel testId="panel-cascade" kicker="FR-REG-004" title="12-layer applicability cascade">
          <div className="divide-y divide-border">
            {(meta?.cascade_layers || []).map((l) => {
              const value = rule.applicability?.[l.key];
              return (
                <div key={l.key} className="flex items-center justify-between gap-2 px-4 py-2"
                     data-testid={`cascade-layer-item-${l.layer}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-5 font-mono text-[10px] text-muted-foreground/70">{String(l.layer).padStart(2, "0")}</span>
                    <span className="text-xs text-muted-foreground">{l.label}</span>
                  </div>
                  <span className={`font-mono text-[10px] ${value ? "text-primary" : "text-muted-foreground/70"}`}>
                    {value ? (Array.isArray(value) ? value.join(" | ") : String(value)) : "ANY"}
                  </span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel testId="panel-conditions" kicker="Predicate" title="Trigger conditions">
          <Table testId="conditions-table" head={<><Th>Field</Th><Th>Operator</Th><Th>Value</Th></>}>
            {(rule.conditions || []).length === 0 && <Empty colSpan={3} label="Applicability-only rule" />}
            {(rule.conditions || []).map((c, i) => (
              <tr key={`${c.field}-${i}`} className="row-hover" data-testid={`condition-row-${i}`}>
                <Td className="font-mono text-primary">{c.field}</Td>
                <Td className="font-mono text-muted-foreground">{c.operator}</Td>
                <Td className="font-mono">{String(c.value)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>

        <Panel testId="panel-tests" kicker="FR-REG-005 / 012" title="Automated test suite"
               actions={lastRun && (
                 <Chip className={lastRun.passed ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                   : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
                   {lastRun.passed_count}/{lastRun.total} passed
                 </Chip>
               )}>
          {!lastRun && <div className="p-6 text-xs text-muted-foreground/70">No test run persisted for this version yet.</div>}
          {lastRun && (
            <div className="divide-y divide-border">
              {lastRun.results.map((r) => (
                <div key={r.type} className="flex items-start gap-2.5 px-4 py-2.5" data-testid={`test-result-${r.type}`}>
                  {r.passed ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />}
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-foreground/80">
                      {r.type.replace(/_/g, " ")}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{r.detail}</div>
                  </div>
                </div>
              ))}
              <div className="px-4 py-2 font-mono text-[10px] text-muted-foreground/70">
                Run by {lastRun.run_by} · {dateTime(lastRun.created_at)}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel testId="panel-evidence" kicker="FR-REG-003" title="Linked evidence">
          <Table testId="rule-evidence-table" head={<><Th>Source</Th><Th>Provision</Th><Th>SHA-256</Th><Th>Status</Th></>}>
            {(data.evidence || []).length === 0 && <Empty colSpan={4} label="No evidence linked to this version" />}
            {(data.evidence || []).map((e) => (
              <tr key={e.id} className="row-hover" data-testid={`rule-evidence-${e.id}`}>
                <Td>
                  <div className="text-foreground">{e.title}</div>
                  <div className="text-[11px] text-muted-foreground">{e.source_name}</div>
                </Td>
                <Td className="font-mono text-[11px]">{e.provision}</Td>
                <Td className="font-mono text-[10px] text-muted-foreground">{hashShort(e.content_hash)}</Td>
                <Td>
                  <Chip className={e.verification_status === "verified"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-border bg-muted text-muted-foreground"}>
                    <ShieldCheck className="h-3 w-3" /> {e.verification_status}
                  </Chip>
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>

        <Panel testId="panel-versions" kicker="FR-REG-002" title="Version history">
          <Table testId="versions-table" head={<><Th>Version</Th><Th>State</Th><Th>Effective</Th><Th>Created by</Th></>}>
            {(data.versions || []).map((v) => (
              <tr key={v.id} className="row-hover" data-testid={`version-row-${v.version}`}>
                <Td>
                  <Link to={`/regulatory/rules/${v.id}`} className="font-mono text-primary hover:underline">
                    v{v.version}
                  </Link>
                  {v.id === rule.id && <span className="ml-2 text-[10px] text-muted-foreground/70">current</span>}
                </Td>
                <Td><LifecycleChip state={v.lifecycle_state} /></Td>
                <Td className="font-mono text-[11px]">
                  {shortDate(v.effective_from)} → {v.effective_to ? shortDate(v.effective_to) : "open"}
                </Td>
                <Td className="text-[11px] text-muted-foreground">{v.created_by}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>

      {(rule.transitions || []).length > 0 && (
        <Panel testId="panel-transitions" kicker="Maker-checker trail" title="Lifecycle transitions">
          <Table testId="transitions-table" head={<><Th>From</Th><Th>To</Th><Th>By</Th><Th>Role</Th><Th>When</Th></>}>
            {rule.transitions.map((t, i) => (
              <tr key={i} className="row-hover">
                <Td className="font-mono text-[11px] text-muted-foreground">{t.from}</Td>
                <Td><LifecycleChip state={t.to} /></Td>
                <Td className="text-[11px]">{t.by}</Td>
                <Td className="font-mono text-[10px] text-muted-foreground">{t.role}</Td>
                <Td className="font-mono text-[11px]">{dateTime(t.at)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      )}

      {rule.lifecycle_state !== "PUBLISHED" && (
        <div className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300"
             data-testid="not-published-notice">
          <CircleSlash className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          This version is not PUBLISHED, so it cannot hard-block a transaction at runtime (FR-REG-007).
        </div>
      )}
    </div>
  );
}

const Meta = ({ label, value, mono }) => (
  <div className="bg-card px-4 py-2.5">
    <div className="kicker">{label}</div>
    <div className={`mt-1 truncate text-xs text-foreground/80 ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);
